'use client'

import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import toast from 'react-hot-toast'
import { authClient } from '@/clients/better-auth'
import { AppUrls, IconIds } from '@/enums'
import { LinkWrapper } from '@/components/common/LinkWrapper'
import { IconWrapper } from '@/components/icons/IconWrapper'
import { useState } from 'react'
import { CenteredContent } from '@/components/app/CenteredContent'
import { AppButton } from '@/components/ui/AppButton'
import { hasResponseStatus, hasStatus, unwrapSafePromise } from '@/utils'
import { AppInput } from '@/components/ui/AppInput'

type FormData = {
    email: string
    password: string
}

export function SignInForm() {
    const searchParams = useSearchParams()
    const callbackURL = searchParams.get('callbackUrl') || AppUrls.DASHBOARD
    const [showPassword, setShowPassword] = useState(false)
    const form = useForm<FormData>({
        defaultValues: { email: '', password: '' },
    })

    const mutation = useMutation({
        mutationFn: async (values: FormData) => {
            if (!values.email.includes('@')) throw new Error('Please enter a valid email address')
            if (!values.password) throw new Error('Password is required')

            // unwrap because we want to handle errors in the onError callback
            return unwrapSafePromise(authClient.signIn.email({ email: values.email, password: values.password, rememberMe: true }))
        },
        onError: (error: unknown) => {
            if ((hasStatus(error) && error.status === 403) || (hasResponseStatus(error) && error.response.status === 403)) {
                window.location.href = '/auth/verify-email'
                return
            }
            toast.error((error as Error)?.message || 'Sign in failed')
        },
        onSuccess: () => {
            window.location.href = callbackURL
        },
    })

    function onSubmit(values: FormData) {
        mutation.mutate(values)
    }

    return (
        <CenteredContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
                <h2 className="text-2xl font-semibold">Sign in</h2>

                {/* Continue as guest */}

                <LinkWrapper
                    href={AppUrls.HOME}
                    className="flex items-center justify-center gap-2 rounded-lg bg-default/5 px-3 py-2.5 text-sm transition-all duration-300 ease-in-out hover:bg-default/15"
                >
                    Continue as guest
                    <IconWrapper id={IconIds.ARROW_RIGHT} className="size-5" />
                </LinkWrapper>

                <div className="flex items-center gap-4">
                    <div className="w-full border-b border-default/10" />
                    <p className="text-center opacity-30">or</p>
                    <div className="w-full border-b border-default/10" />
                </div>

                {/* Sign in with Okta FastPass */}
                {/* <StyledTooltip content="To be implemented - if need be" placement="top">
                <button
                    type="button"
                    disabled={true}
                    className="flex cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-default/5 px-3 py-2.5 transition-all duration-300 ease-in-out hover:bg-default/15"
                >
                    <IconWrapper id={IconIds.OKTA} className="size-5" />
                    Sign in with Okta FastPass
                </button>
            </StyledTooltip> */}
                {/* <div className="flex items-center gap-4">
                <div className="w-full border-b border-default/10" />
                <p className="text-center opacity-30">or</p>
                <div className="w-full border-b border-default/10" />
            </div> */}

                {/* Email and password */}
                <div>
                    <label>
                        <p className="text-sm">Email</p>
                        <AppInput
                            type="email"
                            {...form.register('email', {
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Please enter a valid email address',
                                },
                            })}
                            placeholder="Your email"
                        />
                    </label>
                    {form.formState.errors.email && <div style={{ color: 'red' }}>{form.formState.errors.email.message}</div>}
                </div>
                <div>
                    <label>
                        <p className="text-sm">Password</p>
                        <div className="relative">
                            <AppInput
                                type={showPassword ? 'text' : 'password'}
                                {...form.register('password', {
                                    required: 'Password is required',
                                })}
                                placeholder="Your password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2"
                            >
                                <IconWrapper id={showPassword ? IconIds.EYE_OFF : IconIds.EYE} className="mr-2 size-5" />
                            </button>
                        </div>
                    </label>
                    {form.formState.errors.password && <div style={{ color: 'red' }}>{form.formState.errors.password.message}</div>}
                </div>
                <AppButton
                    type="submit"
                    loading={mutation.isPending}
                    loadingText="Signing in..."
                    disabled={!mutation.isPending && !form.formState.isValid}
                >
                    Sign in
                </AppButton>

                {/* other actions */}
                <LinkWrapper href={AppUrls.SIGN_UP} className="mx-auto hover:underline">
                    <p>Don't have an account? Sign up</p>
                </LinkWrapper>
                <LinkWrapper href={AppUrls.FORGET_PASSWORD} className="mx-auto hover:underline">
                    <p>Forgot password?</p>
                </LinkWrapper>
            </form>
        </CenteredContent>
    )
}
